import { Chip, DialogActions, DialogContent, DialogTitle, IconButton, TextField } from "@material-ui/core";
import { ArrowBackIos, Close as CloseIcon } from "@material-ui/icons";
import React, { useState } from "react";
import TagsInput from "react-tagsinput";
import type Stripe from "stripe";
import styled from "styled-components";
import { WorkspacePlanTier, WorkspaceUserRole } from "common/interfaces";
import stripeProducts, { RecurringPlanDefinition } from "common/stripeProducts";
import Analytics from "js/analytics";
import { Button } from "js/Components/Button";
import { getStaticUrl } from "js/config";
import BillingController, { BillingControllerState, upgradeWorkspacePlan } from "js/controllers/BillingController";
import WorkspaceController, {
    inviteWorkspaceUsers,
    setPersistentWorkspaceId,
    WorkspaceControllerState,
} from "js/controllers/WorkspaceController";
import { BeautifulDialog, ShowDialog, ShowDialogAsync, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import { Gap10, Gap20, Gap30 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { TAG_DELIMITERS } from "js/react/constants";
import { themeColors } from "js/react/sharedStyles";
import { TermsOfUseAndRefundPolicy } from "js/react/views/Billing/Checkout/components/TermsOfUseAndRefundPolicy/TermsOfUseAndRefundPolicy";
import SuccessfulPurchaseDialog from "js/react/views/Billing/dialogs/SuccessfulPurchaseDialog";
import { defaultText, settingsLabel } from "js/textStyles";

import ChangePaymentMethodDialog from "./ChangePaymentMethodDialog";
import { PaymentMethodSection } from "./components/PaymentMethodSection";
import { SummarySection } from "./components/SummarySection";
import useUpgradeToTeamForm from "./hooks/useUpgradeToTeamForm";
import * as StripeUtils from "common/utils/stripeUtils";
import { t } from "js/i18n";

const StyledDialogTitle = styled(DialogTitle)`
    h2 {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 20px;
    }
`;

const StyledDialogActions = styled(DialogActions)`
    &&& {
        justify-content: space-between;
    }
`;

const StyledIconButton = styled(IconButton)`
    &&& {
        position: absolute;
        top: 20px;
        left: 20px;
        color: ${themeColors.ui_blue};
    }
`;

const SectionTitle = styled.div`
    ${settingsLabel}
`;

const SectionName = styled.div`
    font-size: 10px;
    line-height: 150%;
    color: ${themeColors.gray};
`;

const SectionContent = styled.div`
    ${defaultText}
    color: ${themeColors.mediumGray};
    font-size: 12px;
`;

const StyledEmailChip = styled(({ isValid, ...props }) => <Chip {...props} />)`
    &&& {
        background: #F1F1F1;
        margin: 2px;

        .MuiChip-label {
            text-align: center;
            font-family: "Source Sans Pro";
            font-size: 13px;
            font-style: normal;
            font-weight: 600;
            line-height: normal;
            color: ${props => props.isValid ? themeColors.mediumGray : "#ff0000"};
        }
    }
`;

const StyledEmailTagsInput = styled(TagsInput) <{ hasError?: boolean; isFocused?: boolean }>`
    width: 100%;
    padding: 6px;
    background-color: #fff;
    border: 1px solid ${props => props.hasError ? "#ff0000" : props.isFocused ? "#11a9e2" : "rgba(0,0,0,0.23)"};
    border-radius: 4px;
    overflow: hidden;
    font-size: 16px;

    &:hover {
        border-color: ${props => props.hasError ? "#ff0000" : props.isFocused ? "#11a9e2" : "#333"};
    }
`;

const EmailTagLayout = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    align-items: flex-start;
    gap: 4px;
    min-height: 40px;
`;

const EmailInput = styled.input`
    background: transparent;
    border: 0;
    color: #222;
    outline: none;
    font-size: 16px;
    min-width: 200px;
    flex: 1;
    flex-shrink: 0;
    height: 32px;
    display: flex;
    align-items: center;
`;

type UpgradeToTeamWorkspaceDialogProps = WorkspaceControllerState & BillingControllerState & {
    closeDialog: (result: Boolean) => void;
    onUpgradeStart?: () => void;
}

const UpgradeToTeamWorkspaceDialog = (props: UpgradeToTeamWorkspaceDialogProps) => {
    const {
        workspace: { id: workspaceId, name: workspaceName },
        plan,
        users,
        stripeData: { subscription, customer },
        closeDialog,
        onUpgradeStart,
    } = props;
    const [showCheckoutStage, setShowCheckoutStage] = useState<boolean>(false);
    const [billingInterval, setBillingInterval] = useState<Stripe.Price.Recurring.Interval>(
        subscription?.current_billing_interval || "year",
    );

    const [paymentMethod, setPaymentMethod] = useState<Stripe.PaymentMethod | null>(BillingController.paymentMethod);
    const taxIdValue = StripeUtils.Customer.getTaxId(customer);
    const taxIdType = StripeUtils.Customer.getTaxIdType(customer);
    const billingAddress = StripeUtils.Customer.getBillingAddress(customer, paymentMethod);

    const tier = WorkspacePlanTier.TEAM;
    const productDefinition = stripeProducts[tier];
    const recurringPlans = (productDefinition.plans.filter(plan => plan.type === "recurring" && plan.isDefault) as RecurringPlanDefinition[]);
    const [prices] = useState<{ year: RecurringPlanDefinition; month?: RecurringPlanDefinition }>(() => ({
        year: recurringPlans.find(plan => plan.interval === "year"),
        month: recurringPlans.find(plan => plan.interval === "month"),
    }));

    const showBillingSwitch = !subscription?.current_billing_interval;
    const planDefinition = prices[billingInterval];
    const priceId = planDefinition?.planId;
    const hasPaymentMethod = paymentMethod?.type;

    // Use the shared hook for team invite logic
    const {
        teamName,
        setTeamName,
        ownerEmails,
        selectedExisting,
        teamMembers,
        emailError,
        isEmailInputFocused,
        setIsEmailInputFocused,
        validateEmail,
        isDuplicateEmail,
        handleTagsChange,
        handlePaste,
        workspaceUsersToInvite,
    } = useUpgradeToTeamForm({ users, workspaceName });

    const seatCount = ownerEmails.length + selectedExisting.length + teamMembers.length;

    const handleBillingIntervalChange = (newInterval: "month" | "year") => {
        setBillingInterval(newInterval);
    };

    const handleSubmit = async () => {
        onUpgradeStart?.();

        closeDialog(true);

        const trialDaysRemaining = StripeUtils.Subscription.getTrialDaysRemaining(subscription);
        const hasTrial = trialDaysRemaining > 0;
        const progressDialog = ShowDialog(ProgressDialog, { title: t("Upgrading to the Team Plan...") });
        try {
            await upgradeWorkspacePlan({
                workspaceId: workspaceId,
                quantity: seatCount,
                tier,
                priceId: priceId,
                // Attach the payment method id if it was collected
                paymentMethodId: !plan.requiresSubscription ? paymentMethod?.id : undefined,
                billingDetails: {
                    address: billingAddress,
                    taxId: taxIdValue && taxIdType ? { type: taxIdType, value: taxIdValue } : undefined,
                },
                promotionCode: undefined,
                analyticsProps: {
                    seat_recipients: [...selectedExisting, ...teamMembers],
                },
            });

            if (workspaceUsersToInvite().length > 0) {
                progressDialog.setTitle(t("Inviting your team members..."));
                await inviteWorkspaceUsers(workspaceId, workspaceUsersToInvite());
            }

            const emailToUid = new Map((users ?? []).map(u => [u?.email?.toLowerCase?.(), u?.uid]));
            const uidsToUpdate = selectedExisting
                .filter(Boolean)
                .map(e => emailToUid.get(e))
                .filter(Boolean) as string[];

            if (uidsToUpdate.length > 0) {
                progressDialog.setTitle(t("Updating existing members..."));
                await Promise.all(
                    uidsToUpdate.map(uid =>
                        WorkspaceController.updateWorkspaceUser({ uid, update: { role: WorkspaceUserRole.MEMBER } }),
                    ),
                );
            }

            setPersistentWorkspaceId(workspaceId, "CREATED");

            Analytics.ModifyWorkspacePlan.UpgradeFlowComplete();

            if (!hasTrial) {
                const dialog = ShowDialog(SuccessfulPurchaseDialog, {
                    productDisplayName: productDefinition.productName,
                    callToActionText: t("Continue"),
                    showTrial: false,
                    onCallToActionClick: () => {
                        dialog.close();
                        closeDialog(true);
                    },
                });
            }
        } catch (err) {
            const errorMessage = String(err?.message || err).toLowerCase();
            const errorCode = err?.code;

            const isPaymentError = errorMessage.includes("payment") ||
                errorMessage.includes("card") ||
                errorMessage.includes("stripe") ||
                errorCode === "card_declined" ||
                errorCode === "expired_card" ||
                errorCode === "insufficient_funds";

            if (isPaymentError) {
                const updated = await ShowDialogAsync(ChangePaymentMethodDialog);
                if (updated) {
                    return;
                }
            }

            ShowErrorDialog({
                title: t("Error upgrading workspace"),
                message: (<>
                    {t("Sorry, we had a problem upgrading your workspace, please contact support at support@beautiful.ai")}
                    <br />
                    {t("Error: {{message}}", { message: err.message })}
                </>)
            });
        } finally {
            progressDialog.close();
        }
    };

    return (
        <BeautifulDialog
            onClose={closeDialog}
        >
            {!showCheckoutStage
                ? (
                    <>
                        <StyledDialogTitle>
                            <img src={getStaticUrl("/images/beautifulai-logos/beautifulai-checkout-logo.svg")} />
                            <div>{t("Create your team")}</div>
                        </StyledDialogTitle>
                        <DialogContent>
                            <FlexBox column left gap={3}>
                                <SectionName>{t("Workspace Name")}</SectionName>
                                <TextField
                                    value={teamName}
                                    onChange={e => setTeamName(e.target.value)}
                                    placeholder={t("Enter your team name")}
                                    fullWidth
                                />
                                <Gap20 />
                                <SectionName>{t("Add Members")}</SectionName>
                                <StyledEmailTagsInput
                                    className="tags-input"
                                    addKeys={TAG_DELIMITERS}
                                    addOnBlur
                                    hasError={!!emailError}
                                    isFocused={isEmailInputFocused}
                                    // Render owners (fixed), selectable existing (non-owner), and new
                                    value={[...ownerEmails, ...selectedExisting, ...teamMembers]}
                                    renderTag={props => {
                                        const value = String(props.tag).toLowerCase();
                                        const isValid = validateEmail(value);
                                        const isFixed = ownerEmails.includes(value);
                                        const allCurrentEmails = [...ownerEmails, ...selectedExisting, ...teamMembers];
                                        const isDuplicate = isDuplicateEmail(value, allCurrentEmails);
                                        return (
                                            <StyledEmailChip
                                                key={props.key}
                                                isValid={isValid && !isDuplicate}
                                                label={value}
                                                onDelete={isFixed ? undefined : () => props.onRemove(props.key)}
                                                deleteIcon={<CloseIcon style={{ width: 12, height: 12 }} />}
                                            />
                                        );
                                    }}
                                    onChange={handleTagsChange}
                                    renderInput={(inputProps: any) => {
                                        const { value, onChange, onKeyDown, onBlur, ref } = inputProps;
                                        const hasRemovableTags = selectedExisting.length + teamMembers.length > 0;
                                        return (
                                            <EmailInput
                                                ref={ref}
                                                value={typeof value === "string" ? value : ""}
                                                onChange={onChange}
                                                onKeyDown={onKeyDown}
                                                onBlur={e => {
                                                    setIsEmailInputFocused(false);
                                                    onBlur(e);
                                                }}
                                                onFocus={() => setIsEmailInputFocused(true)}
                                                onPaste={handlePaste}
                                                placeholder={hasRemovableTags ? "" : t("Type or paste emails...")}
                                            />
                                        );
                                    }}
                                    renderLayout={(tagComponents, inputComponent) => (
                                        <EmailTagLayout>
                                            {tagComponents}
                                            {inputComponent}
                                        </EmailTagLayout>
                                    )}
                                />
                                {emailError && (
                                    <div style={{ color: "red", fontSize: 12 }}>{emailError}</div>
                                )}
                            </FlexBox>
                        </DialogContent>
                        <StyledDialogActions>
                            <Button unframed color={themeColors.mediumGray} onClick={() => closeDialog(false)}>{t("Cancel")}</Button>
                            <Button blue onClick={() => setShowCheckoutStage(true)}
                                disabled={!teamName || emailError}>{t("Continue")}</Button>
                        </StyledDialogActions>
                    </>
                )
                : (
                    <>
                        <StyledIconButton
                            size="small"
                            onClick={() => setShowCheckoutStage(false)}>
                            <ArrowBackIos style={{ fontSize: "16px", paddingLeft: "5px" }} />
                        </StyledIconButton>
                        <StyledDialogTitle>
                            <img src={getStaticUrl("/images/beautifulai-logos/beautifulai-checkout-logo.svg")}
                                alt={t("Beautiful AI")} />
                            <div>{t("Complete checkout")}</div>
                        </StyledDialogTitle>
                        <DialogContent>
                            <SummarySection
                                billingInterval={billingInterval}
                                tier={tier}
                                subscription={subscription}
                                seats={seatCount}
                                showBillingSwitch={showBillingSwitch}
                                onBillingIntervalChange={handleBillingIntervalChange}
                            />
                            <Gap30 />
                            <SectionTitle>{t("Payment method")}</SectionTitle>
                            <PaymentMethodSection
                                paymentMethod={paymentMethod}
                                setPaymentMethod={setPaymentMethod} />
                            <Gap10 />
                            <FlexBox left column fillWidth>
                                <SectionContent>
                                    {t("Your subscription will automatically renew {{interval}}.", {
                                        interval: billingInterval === "month" ? t("monthly") : t("annually")
                                    })}
                                </SectionContent>
                                <TermsOfUseAndRefundPolicy />
                            </FlexBox>
                        </DialogContent>
                        <StyledDialogActions>
                            <Button unframed color={themeColors.mediumGray} onClick={closeDialog}>{t("Cancel")}</Button>
                            <Button blue disabled={!hasPaymentMethod} onClick={handleSubmit}>{t("Start team plan")}</Button>
                        </StyledDialogActions>
                    </>
                )
            }
        </BeautifulDialog>
    );
};

export default WorkspaceController.withInitializedState(BillingController.withState(UpgradeToTeamWorkspaceDialog));
